import { Link, useOutletContext } from 'react-router-dom';
import { Avatar, Badge, Button, Card, IconHighlight, Skeleton, StatusMessage } from '@aimsos/ui';
import { Trans, useTranslation } from '@aimsos/i18n';
import { AlertTriangle, Check, Circle, LayoutGrid } from 'lucide-react';
import { useWorkspaceVisibility } from '../../hooks/provider';

const K = 'integrations.distribution.workspaceAvailability';

const AVATAR_CLASS =
  '!h-9 !w-9 !min-h-9 !min-w-9 !rounded-[8px] !border-0 text-[13px] font-extrabold text-white';
const STATUS_ON =
  '!h-auto !gap-1 !border-[rgba(16,185,129,0.32)] !bg-[rgba(16,185,129,0.10)] !px-2.5 !py-[3px] !text-[10.5px] !font-bold !text-[#34d39c]';
const STATUS_OFF =
  '!h-auto !gap-1 !border-edge !bg-white/[0.03] !px-2.5 !py-[3px] !text-[10.5px] !font-bold !text-content-faint';

/**
 * Compact switch matching the prototype `.ws-switch` (no Switch in `@aimsos/ui`).
 */
function WorkspaceSwitch({ on, label, onClick }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      onClick={onClick}
      className={[
        'relative h-[22px] w-[38px] shrink-0 rounded-full p-0 transition-colors',
        on ? 'bg-[#34d39c]' : 'bg-[#3A3D45] dark:bg-gray-600',
      ].join(' ')}
    >
      <span
        aria-hidden="true"
        className={[
          'absolute top-[3px] left-[3px] h-4 w-4 rounded-full bg-white shadow transition-transform',
          on ? 'translate-x-4' : 'translate-x-0',
        ].join(' ')}
      />
    </button>
  );
}

/**
 * Provider Detail — Workspace availability tab (CT-4208). Prototype `detailWorkspaces`:
 * hero summary + enable/disable all, then one row per workspace (avatar, meta,
 * Available/Disabled chip, switch).
 */
const WorkspaceAvailabilityTab = () => {
  const { t } = useTranslation('admin-studio');
  const { provider } = useOutletContext();
  const { directory, enabledNames, enabledCount, total, isLoading, error, toggle, enableAll, disableAll } =
    useWorkspaceVisibility(provider.slug);

  if (isLoading) return <Skeleton className="h-64 w-full" />;
  if (error) return <StatusMessage icon={AlertTriangle} title={t('common.requestFailed')} subtitle={error} />;

  return (
    <Card>
      <Card.Header
        title={t(`${K}.title`)}
        action={<span className="text-xs text-content-faint">{t(`${K}.meta`)}</span>}
        className="!px-5 !py-3.5"
      />
      <Card.Body className="!px-5 !py-4">
        <div className="mb-3.5 flex flex-wrap items-center justify-between gap-3.5 rounded-[11px] border border-[#2B7FFF]/30 bg-gradient-to-br from-[#2B7FFF]/[0.06] to-[#A78BFA]/[0.05] px-4 py-3.5">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <IconHighlight
              icon={LayoutGrid}
              size="medium"
              bgColor="Primary"
              iconColor="PrimaryAlt"
              className="border border-[#2B7FFF]/32"
            />
            <div className="min-w-0">
              <p className="text-[13.5px] font-bold tracking-[-0.01em] text-content">
                {t(`${K}.heroTitle`, { enabled: enabledCount, total })}
              </p>
              <p className="mt-[3px] text-[11.5px] leading-snug text-content-secondary">
                {t(`${K}.heroDesc`, { name: provider.name })}
              </p>
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={disableAll}
              disabled={enabledCount === 0}
              className="hover:!border-red-500/55 hover:!bg-red-500/[0.06] hover:!text-red-300"
            >
              {t(`${K}.disableAll`)}
            </Button>
            <Button variant="secondary" size="sm" onClick={enableAll} disabled={enabledCount === total}>
              {t(`${K}.enableAll`)}
            </Button>
          </div>
        </div>

        <ul className="flex flex-col gap-2">
          {directory.map((ws) => {
            const on = enabledNames.has(ws.name);
            return (
              <li
                key={ws.id}
                className={[
                  'grid grid-cols-[auto_1fr_auto_auto] items-center gap-3.5 rounded-[10px] border border-edge bg-white/[0.02] px-3.5 py-3',
                  on ? '' : 'opacity-[0.55]',
                ].join(' ')}
              >
                <Avatar
                  alt={ws.name}
                  initials={ws.initials}
                  className={`${AVATAR_CLASS} ${ws.avatarClassName}`}
                />
                <div className="min-w-0">
                  <div className="truncate text-[13px] font-bold leading-tight text-content">{ws.name}</div>
                  <div className="mt-[3px] truncate text-[11px] text-content-faint">
                    {t(`${K}.members`, { count: ws.member_count })}
                    <span className="px-1.5">·</span>
                    {ws.desc}
                    <span className="px-1.5">·</span>
                    {t(`${K}.owner`, { name: ws.owner })}
                  </div>
                </div>
                <Badge
                  size="small"
                  icon={on ? Check : Circle}
                  className={on ? STATUS_ON : STATUS_OFF}
                >
                  {on ? t(`${K}.available`) : t(`${K}.disabled`)}
                </Badge>
                <WorkspaceSwitch
                  on={on}
                  label={t(on ? `${K}.disableOne` : `${K}.enableOne`, { workspace: ws.name })}
                  onClick={() => toggle(ws.name)}
                />
              </li>
            );
          })}
        </ul>

        <p className="mt-3.5 rounded-[9px] border border-dashed border-edge bg-white/[0.02] px-3 py-2.5 text-[11px] leading-relaxed text-content-faint">
          <Trans
            ns="admin-studio"
            i18nKey={`${K}.note`}
            components={{
              note: <span className="font-semibold text-content-secondary" />,
              access: <Link to="../instances" className="text-[#2B7FFF] no-underline hover:underline" />,
              distribution: (
                <Link to="/admin-studio/distribution" className="text-[#2B7FFF] no-underline hover:underline" />
              ),
            }}
          />
        </p>
      </Card.Body>
    </Card>
  );
};

export default WorkspaceAvailabilityTab;
